/**
 * Generating a triangle mesh from design material SDF patterns for simple debug visualization of export geometry conversion.
 * @module
 */
import assert from "assert";
import type { Face } from "geom/brep";
import { faceOrNullCopy } from "geom/brep";
import { planeMoveAlongNormal } from "geom/plane";
import { tessFace3, TriangulationStrategy } from "geom/tess";
import type { UVGeneration } from "geom/uv";
import type * as THREE from "three";
import { mesh3ToTHREE, mesh3ToTHREEGeometry } from "../../convert";
import type { DmTransparencyMode, SdfParamsPreprocessed } from "../designMaterialTypes";
import { generateMesh3WithinFaces } from "./generateMesh3WithinFaces";
import type { AdjustGeoType } from "./utils";

export function generateThreeGeometryWithinFaces(
  faces: Face[],
  adjustBaseGeo: AdjustGeoType,
  sdf: SdfParamsPreprocessed,
  transparency: DmTransparencyMode,
  uvGeneration: UVGeneration,
): THREE.BufferGeometry[] {
  const [faceMeshes, faceInwardPush] = generateMesh3WithinFaces(
    faces,
    adjustBaseGeo,
    sdf,
    uvGeneration,
    undefined,
    [],
    {
      mergeCoplanarFaces: false,
      generateMissingNormals: true,
      generatePatternInstances: false,
      expandCellsAtInsideCornerMiters: false,
    },
  );
  const geo = faceMeshes.filter((mesh) => mesh !== undefined).map((mesh) => mesh3ToTHREE(mesh).geometry);
  if (transparency !== "sdfTransparent" && transparency !== "metalScreen") {
    for (let faceId = 0; faceId < faces.length; faceId++) {
      const face = faceOrNullCopy(faces[faceId]);
      assert(face);
      // Move base face behind detail geometry
      planeMoveAlongNormal(face.plane, -faceInwardPush[faceId]);
      // Flip the plane - not sure why this is needed though.
      face.plane.uX = -face.plane.uX;
      face.plane.uY = -face.plane.uY;
      face.plane.uZ = -face.plane.uZ;
      const pushedInFaceMesh = tessFace3(face, undefined, uvGeneration, TriangulationStrategy.EARCUT);
      geo.push(mesh3ToTHREEGeometry(pushedInFaceMesh));
    }
  }
  return geo;
}
